import { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import PeopleIcon from "@mui/icons-material/People";
import TuneIcon from "@mui/icons-material/Tune";
import { MaintenanceSetupTab } from "./MaintenanceSetupTab";
import { MaintenanceApproverListTab } from "./MaintenanceApproverListTab";
import { GeneralMessage, useGeneralMessage } from "../../../../../common/APICommon";

export function ApproverMaintenanceModal({
  isOpen,
  onClose,
  selectedData,
  onSave,
  approverMaintenanceTypes,
  loading,
  mode // Added mode for consistency (e.g., "view", "edit")
}) {
  const isViewOnly = mode === "view";
  const [activeTab, setActiveTab] = useState("setup");
  const [hierarchyCount, setHierarchyCount] = useState(1);
  const [levels, setLevels] = useState([[{ approverId: "", fullName: "" }]]);

  // State for the specific maintenance fields
  const [setupData, setSetupData] = useState({
    maintenanceTypeId: "",
    maintenanceValueId: "",
    isActive: true
  });

  // Validation state matching FinancialApproverModal
  const [errorState, setErrorState] = useState({
    hierarchyCount: false,
    levels: [],
    duplicateApprover: false,
    duplicateApproverName: ""
  });

  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  // Validation logic identical to Financial modal structure
  const validateUniqueness = () => {
    const allApproverIds = [];
    let hasDuplicate = false;
    let duplicateApproverName = "";

    levels.forEach((level) => {
      level.forEach((slot) => {
        if (slot.approverId && slot.approverId !== "") {
          const approverIdNum = slot.approverId;
          if (allApproverIds.includes(approverIdNum)) {
            hasDuplicate = true;
            duplicateApproverName = slot.fullName;
          } else {
            allApproverIds.push(approverIdNum);
          }
        }
      });
    });

    return { hasDuplicate, duplicateApproverName };
  };

  const validate = () => {
    const newErrors = {
      hierarchyCount: false,
      levels: [],
      duplicateApprover: false,
      duplicateApproverName: ""
    };
    let valid = true;

    // Validate Setup
    if (!setupData.maintenanceTypeId || !setupData.maintenanceValueId) {
      newErrors.hierarchyCount = true; // Visual indicator for setup tab
      valid = false;
    }

    // Validate Levels
    const levelErrors = levels.map((level) => {
      const hasAtLeastOne = level.some((slot) => slot.approverId !== "");
      if (!hasAtLeastOne) {
        valid = false;
        return true;
      }
      return false;
    });
    newErrors.levels = levelErrors;

    const { hasDuplicate, duplicateApproverName } = validateUniqueness();

    if (hasDuplicate) {
      newErrors.duplicateApprover = true;
      newErrors.duplicateApproverName = duplicateApproverName;
      valid = false;
    }

    setErrorState(newErrors);
    return valid;
  };

  // Sync logic
  useEffect(() => {
    if (!isOpen) return;

    if (selectedData) {
      setSetupData({
        maintenanceTypeId: selectedData.maintenanceTypeId,
        maintenanceValueId: selectedData.maintenanceValueId,
        isActive: selectedData.isActive === true ? true : false
      });
      setHierarchyCount(selectedData.approverHierarchyCount || 1);

      const mappedLevels = Array.from({ length: selectedData.approverHierarchyCount }, (_, i) => {
        const h = i + 1;
        const slots = selectedData.mainApproverDetails?.filter(d => d.hierarchy === h) || [];
        return slots.length
          ? slots.map(s => ({ approverId: s.approverId, fullName: s.approverFullName }))
          : [{ approverId: "", fullName: "" }];
      });
      setLevels(mappedLevels);
    } else {
      setSetupData({ 
        maintenanceTypeId: "", 
        maintenanceValueId: "", 
        isActive: true 
      });
      setHierarchyCount(1);
      setLevels([[{ approverId: "", fullName: "" }]]);
    }

    setErrorState({ hierarchyCount: false, levels: [], duplicateApprover: false });
    setActiveTab("setup");
  }, [isOpen, selectedData]);

  // Adjust levels when hierarchy count changes
  useEffect(() => {
    setLevels(prev => {
      const current = [...prev];
      if (hierarchyCount > current.length) {
        for (let i = current.length; i < hierarchyCount; i++) current.push([{ approverId: "", fullName: "" }]);
      } else {
        current.splice(hierarchyCount);
      }
      return current;
    });
  }, [hierarchyCount]);

  const handleClose = () => {
    setActiveTab("setup");
    onClose();
  };

  const handleSubmit = async () => {
    if (!validate()) {
      if (errorState.levels.some(Boolean) || errorState.duplicateApprover) {
        setActiveTab("approvers");
      }
      return;
    }

    const details = [];
    levels.forEach((lvl, idx) => {
      lvl.forEach(slot => {
        if (slot.approverId) details.push({ approverId: slot.approverId, hierarchy: idx + 1 });
      });
    });

    const payload = {
      ...setupData,
      approverHierarchyCount: hierarchyCount,
      details
    };

    const result = await onSave(payload, selectedData?.id);

    if (result.success) {
      handleClose();
    } else {
      showGeneralMessage(result.error, false);
    }
  };

  if (!isOpen) return null;

  const tabClass = (tabName) =>
    `px-4 py-3 text-sm font-medium rounded-t-lg transition-colors border-b-2 cursor-pointer border-none bg-transparent ${activeTab === tabName
      ? "border-blue-500 text-blue-600 bg-blue-50"
      : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
    }`;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center overflow-auto bg-black/50 p-3 text-gray-700">
      <div className="relative flex h-auto max-h-[90vh] w-full flex-col rounded-lg bg-gray-50 shadow-xl md:w-[70%] lg:w-[60%] xl:w-[50%]">

        {/* Sticky Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-lg border-b border-gray-200 bg-white px-6 py-3 shadow-sm">
          <h2 className="flex items-center space-x-2 text-xl font-semibold text-gray-800">
            <TuneIcon className="text-blue-500" fontSize="small" />
            <span className="text-lg text-gray-600">
              {selectedData ? "Update Maintenance Approver Config" : "New Maintenance Approver Config"}
            </span>
          </h2>
          <button onClick={handleClose} className="border-none bg-transparent p-1 text-gray-400 hover:text-gray-700 cursor-pointer">
            <CloseIcon fontSize="medium" />
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-gray-200 bg-white pt-3">
          <div className="flex space-x-1 px-6">
            <button onClick={() => setActiveTab("setup")} className={tabClass("setup")}>
              <SettingsIcon fontSize="small" className="mr-1" /> Setup
            </button>
            <button onClick={() => setActiveTab("approvers")} className={tabClass("approvers")}>
              <PeopleIcon fontSize="small" className="mr-1" /> Approvers
            </button>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto p-5">
          {activeTab === "setup" && (
            <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
              <MaintenanceSetupTab
                data={setupData}
                setData={setSetupData}
                count={hierarchyCount}
                setCount={setHierarchyCount}
                approverMaintenanceTypes={approverMaintenanceTypes}
                isEdit={!!selectedData}
                isViewOnly={isViewOnly}
                errorState={errorState}
              />
            </div>
          )}

          {activeTab === "approvers" && (
            <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
              <MaintenanceApproverListTab
                levels={levels}
                setLevels={setLevels}
                count={hierarchyCount}
                isViewOnly={isViewOnly}
                errorState={errorState}
                showGeneralMessage={showGeneralMessage}
              />
            </div>
          )}
        </div>

        {/* Sticky Footer */}
        <div className="flex items-center justify-end space-x-2 rounded-b-lg border-t border-gray-200 bg-white px-5 py-4 shadow-sm">
          <button
            onClick={handleClose}
            className="cursor-pointer rounded border-none bg-gray-400 px-4 py-2 text-sm text-white hover:bg-gray-500 transition-colors"
          >
            Cancel
          </button>
          {!isViewOnly && (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="cursor-pointer rounded border-none bg-blue-500 px-4 py-2 text-sm text-white hover:bg-blue-600 disabled:opacity-60 shadow-sm"
            >
              {loading ? "Saving..." : "Save Configuration"}
            </button>
          )}
        </div>
      </div>

      <GeneralMessage form={showMessage} message={message} isSuccess={isSuccess} />
    </div>
  );
}